// Looker to Amazon Quick page content. Same shape as domo.js; rendered by
// PlatformMigrationContent (components/BiPlatformPage).
export const lookerPage = {
  whyMove: {
    eyebrow: 'Why move',
    title: 'Why teams move from Looker to Amazon Quick',
    intro: 'The reasons we hear most from Looker customers running on AWS.',
    cards: [
      { title: 'Your data is already on AWS', description: 'Keep analytics next to Redshift, S3, and Athena instead of querying them from another cloud.' },
      { title: 'Keep the semantic layer work', description: 'LookML views, explores, and measures map to Quick datasets, so the modeling you invested in carries over.' },
      { title: 'AI agents on the same data', description: 'Once dashboards are on Quick, Chat Agents, Flows, and Research work on the same governed datasets.' },
      { title: 'Spend that counts on AWS', description: 'Quick and the AWS services under it can draw down your existing AWS commitment.' },
    ],
  },

  metadata: {
    id: 'metadata',
    eyebrow: 'Built on your Looker metadata',
    title: 'We read your Looker instance through its API and LookML',
    intro: 'Instead of weeks of interviews, we pull metadata straight from Looker and your LookML repository. Discovery takes days, every decision traces back to real usage, and the same metadata drives conversion, testing, and training.',
    columns: ['What we pull', 'From', 'How we use it'],
    rows: [
      { what: 'LookML models, views, and explores', from: 'LookML Git repository and Looker API', use: 'Map views and explores to Quick datasets and joins' },
      { what: 'Dimensions, measures, and derived tables', from: 'LookML and Looker API', use: 'Convert measures to calculated fields and rebuild derived tables in Redshift' },
      { what: 'Looks and dashboards', from: 'Looker API', use: 'Rebuild visuals and spot duplicate Looks and tiles' },
      { what: 'Access filters, model sets, and user attributes', from: 'LookML and Looker API', use: 'Recreate the same row-level security on Amazon Quick' },
      { what: 'Users, groups, and roles', from: 'Looker API', use: 'Map to SSO and IAM groups and plan training by role' },
      { what: 'Query and dashboard history', from: 'System Activity', use: 'Find the most-used and unused content to rank waves and retire dead content' },
      { what: 'Connections, schedules, and embeds', from: 'Looker API', use: 'Plan connectivity, scheduled reports, and the move to Quick Sight embedding' },
    ],
    benefits: {
      eyebrow: 'What you get from it',
      items: [
        { title: 'Full inventory and lineage', description: 'Every tile traced back through its explore and view to the source table.' },
        { title: 'Complexity score per dashboard', description: 'Liquid, derived tables, and access filters scored, so each wave is sized realistically.' },
        { title: 'Wave plan from real usage', description: 'Waves ranked by System Activity, not by who asks loudest.' },
        { title: 'UAT test cases, generated', description: 'Expected values pulled from Looker through the API and compared with Quick automatically.' },
      ],
      note: {
        lead: 'Read-only access.',
        text: 'Your Looker admin creates API credentials for a user with a read-only role. Nothing in Looker changes until you retire it.',
      },
    },
  },

  process: {
    eyebrow: 'How a Looker migration runs',
    title: 'Automated and AI-led, wave by wave',
    intro: 'Steps 3 to 5 repeat for each wave, so your team reviews exceptions instead of testing every tile.',
    steps: [
      { tag: 'Automated', title: 'Automated assessment', description: 'Metadata pulled from the Looker API and LookML: models, explores, Looks, dashboards, access filters, and usage.' },
      { tag: 'AI-led', title: 'Wave planning', description: 'Content grouped into waves by usage, shared explores, and business unit. Unused Looks are flagged to retire.' },
      { tag: 'Automated', title: 'Automated conversion', description: 'Explores rebuilt as Quick datasets, LookML measures converted to calculated fields, and tiles rebuilt as visuals.' },
      { tag: 'AI-led', title: 'AI-led UAT and QA', description: 'Every migrated dashboard compared with its Looker original. Your team signs off on exceptions only.' },
      { tag: 'Automated', title: 'Enablement and training', description: 'Training per wave, built from who uses which Looker content today.' },
    ],
  },

  afterMigration: {
    eyebrow: 'After the migration',
    title: 'More than dashboards: AI on the same data',
    items: [
      { title: 'Ask the data', description: 'A Chat Agent answers questions on the same datasets your old Looker explores used, with sources.' },
      { title: 'Automate the follow-up', description: 'Quick Flows turn a threshold alert into a task, an email, or a ticket.' },
      { title: 'Research with context', description: 'Quick Research blends your internal numbers with outside sources in one report.' },
    ],
  },

  showFunding: true,
};
